import {
  Add,
  Button,
  EmptyState,
  HeaderSection,
  NotStartedIcon
} from '@lmc/ui-kit'
import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import Actions from '../../../actions'
import { getNominative, showToast } from '../../../common/utils'
import { HelpLink, Spinner } from '../../../components/generic'
import Operations from '../../../operations'
import Selectors from '../../../selectors'
import NoZone from './components/NoZone'
import ZonesItem from './components/ZonesItem'
import { ZonesPaywall } from './components/ZonesPaywall'

export class ZonesScreen extends React.Component {
  componentDidMount() {
    this.props.fetchLists()
  }

  handleDelete = () => {
    this.props.refresh()
    showToast({ type: 'info', title: 'Deleted a Zone' })
  }

  handleEditResident = (resident) => {
    const { openEditResidentModal, zones } = this.props
    openEditResidentModal({ resident, zones })
  }

  handleOpenEdit = (zone) => {
    this.props.openEditModal({
      ...zone,
      residents: zone.residents.map((resident) => resident.id)
    })
  }

  renderZones = (zonesWithResidents) => {
    if (!zonesWithResidents.length)
      return (
        <EmptyState
          title={NO_ZONES_HEADING}
          description={getNoZonesMessage(this.props.preferredPeopleNominative)}
          icon={<NotStartedIcon />}
        />
      )

    const formattedZones = zonesWithResidents.map((zone, index) => {
      if (index === 0) {
        return (
          <div style={styles.firstZoneContainer}>
            <ZonesItem
              key={zone.id}
              onDelete={this.handleDelete}
              onEdit={() => this.handleOpenEdit(zone)}
              onEditResident={(resident) => this.handleEditResident(resident)}
              zone={zone}
              preferredPeopleNominative={this.props.preferredPeopleNominative}
            />
          </div>
        )
      }

      return (
        <div style={styles.zoneContainer}>
          <ZonesItem
            key={zone.id}
            onDelete={this.handleDelete}
            onEdit={() => this.handleOpenEdit(zone)}
            onEditResident={(resident) => this.handleEditResident(resident)}
            zone={zone}
            preferredPeopleNominative={this.props.preferredPeopleNominative}
          />
        </div>
      )
    })

    return formattedZones
  }

  render() {
    const {
      openCreateModal,
      zonesWithResidents,
      unassignedResidents,
      features,
      zones
    } = this.props

    if (!features.features?.zones?.enabled) {
      return (
        <ZonesPaywall
          preferredPeopleNominative={getNominative(
            this.props.preferredPeopleNominative,
            'service users'
          ).toLocaleLowerCase()}
        />
      )
    }

    if (!zonesWithResidents || !unassignedResidents || !zones)
      return <Spinner />

    return (
      <div style={styles.mainContainer}>
        <div style={styles.content}>
          <HeaderSection
            title="Zones"
            rightChildren={
              <Button
                variant="primary"
                id="add-zone"
                data-cy="add-zone-btn"
                style={styles.button}
                type="primary"
                onClick={openCreateModal}
                icon={<Add />}
              >
                Add Zone
              </Button>
            }
          />

          <div style={styles.zonesList}>
            {this.renderZones(zonesWithResidents)}
            <NoZone
              onEditResident={(resident) => this.handleEditResident(resident)}
              residents={unassignedResidents}
            />
            <HelpLink
              supportLink={SUPPORT_ARTICLE}
              style={styles.supportLink}
            />
          </div>
        </div>
      </div>
    )
  }
}

const NO_ZONES_HEADING = "You haven't added any zones yet"
const SUPPORT_ARTICLE =
  'https://support.logmycare.co.uk/pro-module/rest-of-pro/how-do-i-set-up-zones'

const getNoZonesMessage = (preferredPeopleNominative) =>
  `Zoning allows your team to only see the ${getNominative(
    preferredPeopleNominative,
    'residents'
  )} they need to.`

const styles = {
  button: {
    color: 'white',
    fontSize: 13,
    float: 'right',
    textShadow: 'none'
  },
  content: {
    maxWidth: 1100,
    margin: '0 auto',
    wordWrap: 'break-word'
  },
  firstZoneContainer: {
    paddingBottom: 20
  },
  mainContainer: {
    padding: '24px 20px 100px 20px',
    overflow: 'auto',
    height: '85vh'
  },
  supportLink: {
    display: 'inline-block',
    float: 'left'
  },
  zoneContainer: {
    paddingBottom: 20,
    paddingTop: 20
  },
  zonesList: {
    position: 'relative',
    margin: '0 auto',
    maxWidth: 1100,
    top: 40
  }
}

ZonesScreen.propTypes = {
  refresh: PropTypes.func,
  openCreateModal: PropTypes.func,
  zonesWithResidents: PropTypes.array,
  unassignedResidents: PropTypes.array,
  features: PropTypes.object,
  zones: PropTypes.array,
  preferredPeopleNominative: PropTypes.string
}

const mapStateToProps = (state) => ({
  zonesWithResidents: Selectors.getZonesWithResidents(state),
  unassignedResidents: Selectors.getResidentsWithoutZone(state),
  features: state.global.home,
  zones: state.data.zones,
  preferredPeopleNominative: state.global.home.preferredPeopleNominative
})

const mapDispatchToProps = (dispatch) => ({
  fetchLists: () => {
    dispatch(Operations.loadList('residents'))
    dispatch(Operations.loadList('zones'))
  },
  refresh: () => {
    dispatch(Actions.clearLists(['residents', 'zones']))
    dispatch(Operations.loadLists(['residents', 'zones']))
  },
  openCreateModal: () => {
    const id = 'addZone'
    dispatch(Actions.openModal({ modalId: id }))
    dispatch(Actions.addForm({ formId: id }))
  },
  openEditModal: (zone) => {
    const id = 'editZone'
    dispatch(Actions.openModal({ modalId: id }))
    dispatch(Actions.addForm({ formId: id, initial: zone }))
  },
  openEditResidentModal: ({ resident, zones }) => {
    const id = 'editResidentZone'
    const unassignedZone = { id: 'Unassigned', name: 'Unassigned' }
    dispatch(Actions.openModal({ modalId: id }))
    dispatch(
      Actions.addForm({
        formId: id,
        initial: resident,
        data: { zones: [...zones, unassignedZone] }
      })
    )
  }
})

export default connect(mapStateToProps, mapDispatchToProps)(ZonesScreen)
